import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Box, Grid, Typography } from '@mui/material';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import { globalBetaGetService } from '../../../../utils/globalApiServices';
import { getNotificationApi } from '../apiServices';
import { checkApiStatus } from '../../../../utils';
import { Pagination, PageLoader } from '../../../../shared_elements';
import NotificationHeader from '../components/NotificationHeader';
import NotificationTabs from '../components/NotificationTabs';
import NotificationToolbar from '../components/NotificationToolbar';
import NotificationCard from '../components/NotificationCard';
import NotificationSummary from '../components/NotificationSummary';
import RecentActivityCard from '../components/RecentActivityCard';
import '../styles/notification.scss';

const getTypeKey = (item) => {
  const label = (item?.notification_type?.label || '').trim().toLowerCase();
  if (label.includes('release')) return 'release';
  if (label.includes('announcement')) return 'announcement';
  if (label.includes('system') || label.includes('alert')) return 'system_alert';
  return 'other';
};

const getReadStatus = (item) => {
  if (item?.status === 1) return { key: 'read', label: 'Read' };
  return getTypeKey(item) === 'system_alert'
    ? { key: 'alert', label: 'Alert' }
    : { key: 'unread', label: 'Unread' };
};

const stripHtml = (html = '') => html.replace(/<[^>]*>?/gm, '');

const formatNotificationDate = (dateString) => {
  const date = moment(dateString);
  if (!date.isValid()) return '--';
  return date.isSame(moment(), 'day') ? `Today, ${date.format('h:mm A')}` : date.format('DD MMM YYYY, HH:mm');
};

const INCLUDE_DEMO_NOTIFICATIONS = true;
const DEMO_NOTIFICATIONS = [
  {
    id: 'demo-release-lease-workflow',
    notification_type: { label: 'Release' },
    subject: 'Lease Management workflow improvements released',
    description: '<p>The latest Lease Management release introduces improved contract setup, invoice workflows, and reporting usability.</p>',
    created_at: '2026-08-04T11:15:00Z',
    status: 0
  },
  {
    id: 'demo-system-alert',
    notification_type: { label: 'System Alert' },
    subject: 'Scheduled platform maintenance',
    description: '<p>SPARTA will undergo scheduled maintenance tonight from 22:00 to 23:00 UTC. Some services may be temporarily unavailable.</p>',
    created_at: '2026-08-04T10:30:00Z',
    status: 0
  },
  {
    id: 'demo-unread-announcement',
    notification_type: { label: 'Announcement' },
    subject: 'New records collaboration tools available',
    description: '<p>New collaboration tools are now available in Records Dataroom. Open this announcement to learn more.</p>',
    created_at: '2026-08-04T09:15:00Z',
    status: 0
  }
];

const addDemoNotifications = (data = {}, page = 1) => {
  if (!INCLUDE_DEMO_NOTIFICATIONS) return data;
  const apiNotifications = Array.isArray(data?.notifications) ? data.notifications : [];
  return {
    ...data,
    notifications: page === 1 ? [...DEMO_NOTIFICATIONS, ...apiNotifications] : apiNotifications
  };
};

const Notification = () => {
  const [notificationInfo, setNotificationInfo] = useState();
  const [isLoading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');

  const getNotificationList = useCallback((query = {}) => {
    const request = { page: 1, per_page: 20, ...query };
    setLoading(true);
    return globalBetaGetService(getNotificationApi({ ...request, timestamp: Date.now() }))
      .then(response => setNotificationInfo(addDemoNotifications(
        checkApiStatus(response) ? response?.data?.data : { notifications: [] },
        request.page
      )))
      .catch(() => setNotificationInfo(addDemoNotifications({ notifications: [] }, request.page)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    getNotificationList({ per_page: 20 });
  }, [getNotificationList]);

  const notifications = useMemo(() => notificationInfo?.notifications || [], [notificationInfo?.notifications]);
  const hasPagination = Boolean(notificationInfo?.pagination?.total > 0);
  const hasActiveFilters = activeTab !== 'all' || Boolean(searchValue) || typeFilter !== 'all' || statusFilter !== 'all';

  const handleMarkAllRead = () => {
    setNotificationInfo(current => ({
      ...current,
      notifications: (current?.notifications || []).map(item => ({ ...item, status: 1 }))
    }));
  };

  const handleMarkRead = (selectedItem) => {
    setNotificationInfo(current => ({
      ...current,
      notifications: (current?.notifications || []).map(item => (
        item === selectedItem || (item?.id && item.id === selectedItem?.id)
          ? { ...item, status: 1 }
          : item
      ))
    }));
  };

  const derived = useMemo(() => {
    const unread = notifications.filter(item => getReadStatus(item).key !== 'read');
    const byType = (key) => notifications.filter(item => getTypeKey(item) === key);
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);
    const releasesThisMonth = byType('release').filter(item => new Date(item?.created_at) >= startOfMonth);

    return {
      unreadCount: unread.length,
      announcementCount: byType('announcement').length,
      releaseCount: byType('release').length,
      systemAlertCount: byType('system_alert').length,
      releasesThisMonthCount: releasesThisMonth.length,
      recentActivity: [...notifications]
        .sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at))
        .slice(0, 3)
    };
  }, [notifications]);

  const tabs = [
    { key: 'all', label: 'All', count: notifications.length },
    { key: 'unread', label: 'Unread', count: derived.unreadCount },
    { key: 'announcement', label: 'Announcements', count: derived.announcementCount },
    { key: 'release', label: 'Releases', count: derived.releaseCount },
    { key: 'system_alert', label: 'System Alerts', count: derived.systemAlertCount }
  ];

  const visibleNotifications = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    const filtered = notifications.filter(item => {
      const typeKey = getTypeKey(item);
      const status = getReadStatus(item);

      const matchesTab = activeTab === 'all'
        || (activeTab === 'unread' ? status.key !== 'read' : typeKey === activeTab);
      const matchesType = typeFilter === 'all' || typeKey === typeFilter;
      const matchesStatus = statusFilter === 'all' || (statusFilter === 'read' ? status.key === 'read' : status.key !== 'read');
      const matchesSearch = !normalizedSearch || `${item?.subject || ''} ${stripHtml(item?.description)}`.toLowerCase().includes(normalizedSearch);

      return matchesTab && matchesType && matchesStatus && matchesSearch;
    });

    return filtered.sort((a, b) => {
      const diff = new Date(b?.created_at) - new Date(a?.created_at);
      return sortOrder === 'newest' ? diff : -diff;
    });
  }, [notifications, activeTab, typeFilter, statusFilter, searchValue, sortOrder]);

  const handleClearFilters = () => {
    setSearchValue('');
    setActiveTab('all');
    setTypeFilter('all');
    setStatusFilter('all');
    setSortOrder('newest');
  };

  return (
    <Box className="notification-page">
      <NotificationHeader onMarkAllRead={handleMarkAllRead} unreadCount={derived.unreadCount} />
      <NotificationTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      <NotificationToolbar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        sortOrder={sortOrder}
        onSortOrderChange={setSortOrder}
        onClearFilters={handleClearFilters}
      />

      <Grid container spacing={2.5} alignItems="flex-start">
        <Grid size={{ xs: 12, lg: 9 }}>
          <Box className="notification-page-results-header">
            <Box>
              <Typography className="notification-page-results-title">Notification feed</Typography>
              <Typography className="notification-page-results-subtitle">{visibleNotifications.length} {visibleNotifications.length === 1 ? 'notification' : 'notifications'} shown</Typography>
            </Box>
            {hasActiveFilters && (
              <Box component="button" type="button" onClick={handleClearFilters} className="notification-page-results-reset">Reset view</Box>
            )}
          </Box>
          <Box className={`notification-page-list${hasPagination && visibleNotifications.length > 0 ? ' has-pagination' : ''}${visibleNotifications.length === 0 ? ' is-empty' : ''}`}>
            {visibleNotifications.length > 0 ? (
              visibleNotifications.map((item, index) => (
                <NotificationCard
                  key={item?.id || index}
                  item={item}
                  typeKey={getTypeKey(item)}
                  status={getReadStatus(item)}
                  description={stripHtml(item?.description)}
                  formattedDate={formatNotificationDate(item?.created_at)}
                  onRead={handleMarkRead}
                />
              ))
            ) : (
              <Box className="notification-page-empty">
                <Box className="notification-page-empty-icon"><SearchOffRoundedIcon /></Box>
                <Typography className="notification-page-empty-title">
                  {searchValue.trim()
                    ? `No results for ${searchValue.trim()}`
                    : hasActiveFilters
                      ? 'No notifications match these filters'
                      : 'You’re all caught up'}
                </Typography>
                <Typography className="notification-page-empty-subtitle">
                  {searchValue.trim()
                    ? 'Try a different search term or reset the view to browse all notifications.'
                    : hasActiveFilters
                      ? 'Adjust the notification type or status filters, or reset the view to display all notifications.'
                      : 'There are no new product releases, announcements or system alerts to display.'}
                </Typography>
              </Box>
            )}
          </Box>
          {hasPagination && visibleNotifications?.length > 0 && (
            <Box className="notification-page-footer">
              <Pagination
                pagination={notificationInfo.pagination}
                stacks={[10, 20, 50]}
                onPageChange={(event, newPage) => getNotificationList({
                  page: newPage + 1,
                  per_page: notificationInfo.pagination.per_page || 20
                })}
                onRowsPerPageChange={(event) => getNotificationList({
                  page: 1,
                  per_page: Number(event.target.value)
                })}
              />
            </Box>
          )}
        </Grid>

        <Grid size={{ xs: 12, lg: 3 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <NotificationSummary
                unreadCount={derived.unreadCount}
                releasesThisMonthCount={derived.releasesThisMonthCount}
                announcementCount={derived.announcementCount}
                systemAlertCount={derived.systemAlertCount}
              />
            </Grid>
            <Grid size={12}><RecentActivityCard items={derived.recentActivity} formatDate={formatNotificationDate} /></Grid>
          </Grid>
        </Grid>
      </Grid>

      {isLoading ? <PageLoader /> : null}
    </Box>
  );
};

export default Notification;